import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';

// Initialize EmailJS with your service ID
// Sign up free at https://www.emailjs.com
// Replace these with your actual IDs from EmailJS dashboard
const EMAILJS_SERVICE_ID = 'service_6ybwn5o';
const EMAILJS_TEMPLATE_ID = 'template_wzbqvok';
const EMAILJS_PUBLIC_KEY = 'fqggx7umT0elR2Uw1';

// Initialize emailjs once
if (EMAILJS_SERVICE_ID && EMAILJS_PUBLIC_KEY) {
  emailjs.init(EMAILJS_PUBLIC_KEY);
}

// ─── Font injection ────────────────────────────────────────────────────────────
const injectLink = (href) => {
  if (!document.querySelector(`link[href="${href}"]`)) {
    const l = Object.assign(document.createElement('link'), { rel: 'stylesheet', href });
    document.head.appendChild(l);
  }
};
injectLink('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,300;12..96,400;12..96,500;12..96,600;12..96,700;12..96,800&family=Lora:ital,wght@0,400;0,500;0,600;1,400;1,500;1,600&display=swap');
injectLink('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css');

// ─── Design tokens
const T = {
  bg:      '#FAFAF7',
  bgWarm:  '#F2EDE4',
  bgCard:  '#FFFFFF',
  bgDark:  '#0E1525',
  text:    '#111827',
  soft:    '#6B7280',
  softer:  '#9CA3AF',
  accent:  '#FDDB1C',
  accentD: '#E8C900',
  border:  '#E8E2D8',
  borderL: '#F0EBE2',
  green:   '#10B981',
  red:     '#EF4444',
  display: "'Lora', Georgia, serif",
  sans:    "'Bricolage Grotesque', system-ui, sans-serif",
};

// ─── Section Intro helper
const SectionIntro = ({ tag, title, subtitle, center = false, light = false }) => (
  <div style={{ textAlign: center ? 'center' : 'left', marginBottom: '48px' }}>
    {tag && (
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: center ? 'center' : 'flex-start', marginBottom: '12px' }}>
        <span style={{ width: '22px', height: '2px', background: T.accent, borderRadius: '1px', display: 'block' }} />
        <span style={{ fontFamily: T.sans, fontSize: '11px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: light ? 'rgba(255,255,255,0.45)' : T.soft }}>{tag}</span>
      </div>
    )}
    <h2 style={{ fontFamily: T.display, fontSize: 'clamp(1.9rem, 4vw, 2.8rem)', fontWeight: 500, color: light ? '#fff' : T.text, margin: '0 0 12px', lineHeight: 1.15, letterSpacing: '-0.4px' }}>
      {title}
    </h2>
    {subtitle && (
      <p style={{ fontFamily: T.sans, fontSize: '15px', color: light ? 'rgba(255,255,255,0.5)' : T.soft, lineHeight: 1.75, margin: 0, maxWidth: center ? '480px' : '460px', marginLeft: center ? 'auto' : 0, marginRight: center ? 'auto' : 0 }}>
        {subtitle}
      </p>
    )}
  </div>
);

// ─── Contact
const Contact = () => {
  const [mob, setMob] = useState(window.innerWidth <= 768);
  const [sm,  setSm]  = useState(window.innerWidth <= 480);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.title = 'Saha Travels | Contact';
  }, []);

  useEffect(() => {
    const r = () => { setMob(window.innerWidth <= 768); setSm(window.innerWidth <= 480); };
    window.addEventListener('resize', r);
    return () => window.removeEventListener('resize', r);
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validate form
      if (!formData.name || !formData.email || !formData.message) {
        throw new Error('Please fill in all required fields');
      }

      // Check if EmailJS is configured
      if (!EMAILJS_SERVICE_ID || !EMAILJS_TEMPLATE_ID || !EMAILJS_PUBLIC_KEY) {
        throw new Error('Email service not configured. Please contact admin.');
      }

      // Send email using EmailJS
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          to_email: 'sahatravels966@gmail.com', // Your email
          from_name: formData.name,
          from_email: formData.email,
          phone: formData.phone || 'Not provided',
          message: formData.message,
        }
      );

      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setFormData({ name: '', email: '', phone: '', message: '' });
      }, 3500);
    } catch (err) {
      console.error('Email error:', err);
      setError(err.message || 'Failed to send message. Please try calling us instead.');
    } finally {
      setLoading(false);
    }
  };

  const px   = mob ? '20px' : '60px';
  const maxW = '1280px';

  const contactDetails = [
    { icon: 'fa-phone-alt',  label: 'Call Us',      value: '+91 86376 58382',        href: 'tel:+918637658382',            sub: 'Available 24/7' },
    { icon: 'fa-envelope',   label: 'Email Us',     value: 'sahatravels966@gmail.com', href: 'mailto:sahatravels966@gmail.com', sub: 'We reply within 2 hrs' },
    { icon: 'fab fa-whatsapp', label: 'WhatsApp',   value: '+91 86376 58382',        href: 'https://wa.me/918637658382?text=Hi%20Saha%20Travels%2C%20I%20want%20to%20book%20a%20ride',   sub: 'Instant response' },
    { icon: 'fa-map-marker-alt', label: 'Location', value: 'Subramaniya Siva Street, Chennai', href: '#map',              sub: 'Tamil Nadu, India' },
  ];

  const inputStyle = (field) => ({
    width: '100%',
    padding: '14px 18px',
    fontFamily: T.sans,
    fontSize: '14px',
    color: T.text,
    background: T.bg,
    border: `1.5px solid ${focusedField === field ? T.accent : T.border}`,
    borderRadius: '14px',
    outline: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    boxShadow: focusedField === field ? `0 0 0 3px rgba(253,219,28,0.18)` : 'none',
    resize: 'none',
    boxSizing: 'border-box',
  });

  return (
    <>
      {/* ── NAV ──────────────────────────────────────────────────────────── */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        background: 'rgba(250,250,247,0.94)', backdropFilter: 'blur(14px)',
        borderBottom: `1px solid ${T.border}`,
        padding: mob ? '14px 20px' : '15px 60px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        willChange: 'transform',
      }}>
        <a href="/" style={{ display: 'flex', alignItems: 'center', gap: '9px', textDecoration: 'none' }}>
          <div style={{ width: '30px', height: '30px', background: T.accent, borderRadius: '7px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <i className="fas fa-car" style={{ fontSize: '12px', color: T.text }} />
          </div>
          <span style={{ fontFamily: T.sans, fontWeight: 800, fontSize: '17px', color: T.text, letterSpacing: '-0.3px' }}>SAHA</span>
          {!mob && <span style={{ fontFamily: T.sans, fontSize: '13px', color: T.softer }}>Travels</span>}
        </a>
        <div style={{ display: 'flex', alignItems: 'center', gap: mob ? '12px' : '28px' }}>
          {!mob && (['Fleet', 'About', 'Contact'].map(n => (
            <a key={n} href={`/${n.toLowerCase()}`} style={{ fontFamily: T.sans, fontSize: '13px', fontWeight: 500, color: n === 'Contact' ? T.text : T.soft, textDecoration: 'none', transition: 'color 0.2s' }}
              onMouseEnter={e => e.target.style.color = T.text}
              onMouseLeave={e => e.target.style.color = n === 'Contact' ? T.text : T.soft}
            >{n}</a>
          )))}
          <a href="tel:+918637658382" style={{
            fontFamily: T.sans, fontSize: '13px', fontWeight: 700,
            background: T.accent, color: T.text,
            padding: mob ? '9px 18px' : '10px 22px', borderRadius: '50px',
            textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '7px',
            boxShadow: '0 3px 14px rgba(253,219,28,0.4)',
            transition: 'transform 0.18s, box-shadow 0.18s',
          }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(253,219,28,0.5)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 3px 14px rgba(253,219,28,0.4)'; }}
          >
            <i className="fas fa-phone" style={{ fontSize: '11px' }} />{mob ? 'Book' : 'Book a Ride'}
          </a>
        </div>
      </nav>

      {/* ── PAGE HERO ────────────────────────────────────────────────────── */}
      <section style={{ minHeight: mob ? '38vh' : '42vh', background: T.bgDark, paddingTop: '78px', position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'center' }}>
        {/* Glow orbs */}
        <div style={{ position: 'absolute', top: '-60px', right: mob ? '-80px' : '-40px', width: mob ? '260px' : '420px', height: mob ? '260px' : '420px', borderRadius: '50%', background: T.accent, opacity: 0.09, filter: 'blur(90px)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '-40px', left: '-40px', width: '260px', height: '260px', borderRadius: '50%', background: T.accent, opacity: 0.05, filter: 'blur(70px)', pointerEvents: 'none' }} />

        <div style={{ maxWidth: maxW, margin: '0 auto', width: '100%', padding: mob ? '40px 20px 48px' : '48px 60px 60px', position: 'relative', zIndex: 1 }}>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '50px', padding: '7px 14px 7px 9px', marginBottom: '24px' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: T.green, display: 'block', boxShadow: '0 0 0 3px rgba(16,185,129,0.2)', animation: 'pulse-green 2s infinite' }} />
            <span style={{ fontFamily: T.sans, fontSize: '12px', fontWeight: 600, color: 'rgba(255,255,255,0.7)' }}>Available 24/7 · Always Here for You</span>
          </motion.div>

          <motion.h1 initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }}
            style={{ fontFamily: T.display, fontSize: mob ? '38px' : sm ? '32px' : 'clamp(2.8rem, 5.5vw, 4.4rem)', fontWeight: 600, lineHeight: 1.08, color: '#fff', margin: '0 0 16px', letterSpacing: '-1.5px' }}>
            Get in Touch<br />
            <em style={{ fontStyle: 'italic', fontWeight: 400, color: T.accent }}>We're Always Ready.</em>
          </motion.h1>

          <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.22 }}
            style={{ fontFamily: T.sans, fontSize: '15px', color: 'rgba(255,255,255,0.45)', lineHeight: 1.75, maxWidth: '480px' }}>
            Book a ride, ask a question, or send us a message — we'll respond faster than you'd expect.
          </motion.p>
        </div>
      </section>

      {/* ── MARQUEE STRIP ────────────────────────────────────────────────── */}
      <div style={{ background: T.accent, overflow: 'hidden', padding: '13px 0', borderTop: `1px solid ${T.accentD}`, borderBottom: `1px solid ${T.accentD}` }}>
        <div style={{ display: 'flex', animation: 'marquee 22s linear infinite', whiteSpace: 'nowrap' }}>
          {Array(3).fill(['✦ Call Us Anytime', '✦ Zero Wait Policy', '✦ 24/7 Support', '✦ Same-Day Bookings', '✦ Chennai Experts', '✦ 4.9★ Customer Rating', '✦ 50,000+ Happy Riders']).flat().map((t, i) => (
            <span key={i} style={{ fontFamily: T.sans, fontSize: '13px', fontWeight: 700, color: T.text, padding: '0 28px', letterSpacing: '0.02em' }}>{t}</span>
          ))}
        </div>
      </div>

      {/* ── CONTACT CARDS ────────────────────────────────────────────────── */}
      <section style={{ background: T.bg, padding: mob ? '60px 20px 0' : '80px 60px 0' }}>
        <div style={{ maxWidth: maxW, margin: '0 auto' }}>
          <SectionIntro
            tag="Reach Out"
            title={<>Four Ways to<br /><em style={{ fontStyle: 'italic', fontWeight: 400 }}>Connect With Us</em></>}
            subtitle="Pick whatever is most convenient — we're reachable across all channels."
          />
          <div style={{ display: 'grid', gridTemplateColumns: sm ? '1fr 1fr' : mob ? '1fr 1fr' : 'repeat(4, 1fr)', gap: mob ? '14px' : '20px', marginBottom: mob ? '56px' : '72px' }}>
            {contactDetails.map((item, i) => (
              <motion.a
                key={i}
                href={item.href}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                style={{
                  background: T.bgCard, border: `1.5px solid ${T.border}`, borderRadius: '20px',
                  padding: mob ? '20px 16px' : '28px 22px', textDecoration: 'none', display: 'flex',
                  flexDirection: 'column', gap: '14px',
                  transition: 'all 0.3s cubic-bezier(0.2,0.9,0.4,1)',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.borderColor = T.accentD;
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 14px 36px rgba(253,219,28,0.22)';
                  e.currentTarget.style.background = T.accent;
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.borderColor = T.border;
                  e.currentTarget.style.transform = 'none';
                  e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.04)';
                  e.currentTarget.style.background = T.bgCard;
                }}
              >
                <div style={{ width: '44px', height: '44px', background: T.bgWarm, border: `1px solid ${T.border}`, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <i className={`fas ${item.icon}`} style={{ fontSize: '17px', color: T.text }} />
                </div>
                <div>
                  <div style={{ fontFamily: T.sans, fontSize: '10px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.soft, marginBottom: '5px' }}>{item.label}</div>
                  <div style={{ fontFamily: T.sans, fontSize: mob ? '12px' : '13px', fontWeight: 700, color: T.text, lineHeight: 1.35, wordBreak: 'break-word' }}>{item.value}</div>
                  <div style={{ fontFamily: T.sans, fontSize: '11px', color: T.softer, marginTop: '4px' }}>{item.sub}</div>
                </div>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* ── FORM + MAP ───────────────────────────────────────────────────── */}
      <section style={{ background: T.bgWarm, padding: mob ? '60px 20px' : '80px 60px' }}>
        <div style={{ maxWidth: maxW, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: mob ? '1fr' : '1.1fr 0.9fr', gap: mob ? '36px' : '56px', alignItems: 'start' }}>

            {/* ── FORM ── */}
            <motion.div
              initial={{ opacity: 0, x: -18 }} whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.65 }} viewport={{ once: true }}
              style={{ background: T.bgCard, border: `1.5px solid ${T.border}`, borderRadius: '24px', padding: mob ? '28px 22px' : '44px', boxShadow: '0 6px 30px rgba(0,0,0,0.05)' }}
            >
              <SectionIntro
                tag="Send a Message"
                title={<>We'll Get Back<br /><em style={{ fontStyle: 'italic', fontWeight: 400, color: T.text }}>To You Soon.</em></>}
              />

              {submitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  style={{ background: T.bgDark, borderRadius: '18px', padding: '36px 28px', textAlign: 'center' }}
                >
                  <div style={{ width: '56px', height: '56px', background: T.accent, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px' }}>
                    <i className="fas fa-check" style={{ fontSize: '22px', color: T.bgDark }} />
                  </div>
                  <div style={{ fontFamily: T.display, fontSize: '22px', fontWeight: 500, color: '#fff', marginBottom: '10px' }}>Message Sent!</div>
<div style={{ fontFamily: T.sans, fontSize: '14px', color: 'rgba(255,255,255,0.5)' }}>We'll get back to you as soon as possible.</div>                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {[
                    { name: 'name',    type: 'text',  placeholder: 'Your full name' },
                    { name: 'email',   type: 'email', placeholder: 'Email address' },
                    { name: 'phone',   type: 'tel',   placeholder: 'Phone number (optional)' },
                  ].map(({ name, type, placeholder }) => (
                    <input
                      key={name}
                      type={type}
                      name={name}
                      placeholder={placeholder}
                      value={formData[name]}
                      onChange={handleChange}
                      onFocus={() => setFocusedField(name)}
                      onBlur={() => setFocusedField(null)}
                      required={name !== 'phone'}
                      disabled={loading}
                      style={inputStyle(name)}
                    />
                  ))}
                  <textarea
                    name="message"
                    rows={5}
                    placeholder="How can we help you today?"
                    value={formData.message}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('message')}
                    onBlur={() => setFocusedField(null)}
                    required
                    disabled={loading}
                    style={{ ...inputStyle('message'), resize: 'vertical', minHeight: '130px' }}
                  />

                  {error && (
                    <div style={{ background: '#FEE2E2', border: `1px solid ${T.red}`, borderRadius: '10px', padding: '12px 16px', fontFamily: T.sans, fontSize: '13px', color: '#991B1B' }}>
                      <i className="fas fa-exclamation-circle" style={{ marginRight: '8px' }} />
                      {error}
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', paddingTop: '8px' }}>
                    <button
                      type="submit"
                      disabled={loading}
                      style={{
                        fontFamily: T.sans, fontSize: '14px', fontWeight: 700,
                        background: loading ? T.softer : T.accent, color: T.text,
                        padding: '14px 30px', borderRadius: '50px', border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
                        display: 'flex', alignItems: 'center', gap: '9px',
                        boxShadow: '0 4px 20px rgba(253,219,28,0.38)',
                        transition: 'transform 0.18s, box-shadow 0.18s',
                        opacity: loading ? 0.6 : 1,
                      }}
                      onMouseEnter={e => { if (!loading) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 28px rgba(253,219,28,0.5)'; } }}
                      onMouseLeave={e => { if (!loading) { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(253,219,28,0.38)'; } }}
                    >
                      <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-paper-plane'}`} style={{ fontSize: '12px' }} /> {loading ? 'Sending...' : 'Send Message'}
                    </button>
                    <a
                      href="tel:+918637658382"
                      style={{
                        fontFamily: T.sans, fontSize: '14px', fontWeight: 600,
                        color: T.text, padding: '14px 22px', borderRadius: '50px',
                        textDecoration: 'none', border: `1.5px solid ${T.border}`,
                        display: 'flex', alignItems: 'center', gap: '8px',
                        transition: 'border-color 0.2s, background 0.2s',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = T.accentD; e.currentTarget.style.background = 'rgba(253,219,28,0.07)'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.background = 'transparent'; }}
                    >
                      <i className="fas fa-phone" style={{ fontSize: '11px' }} /> Call Instead
                    </a>
                  </div>
                </form>
              )}
            </motion.div>

            {/* ── MAP + INFO ── */}
            <motion.div
              initial={{ opacity: 0, x: 18 }} whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.65 }} viewport={{ once: true }}
              style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}
            >
              {/* Map */}
              <div style={{ borderRadius: '20px', overflow: 'hidden', border: `1.5px solid ${T.border}`, boxShadow: '0 4px 18px rgba(0,0,0,0.06)', height: mob ? '240px' : '310px' }}>
                <iframe
                  id="map"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2875.532768675741!2d80.19905115980897!3d13.032311718809016!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a5267006653c9eb%3A0xdff8381770ab7cbd!2sSubramaniya%20siva%20street!5e0!3m2!1sen!2sin!4v1771866183318!5m2!1sen!2sin"
                  title="SAHA Travels Chennai Office"
                  style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
                  loading="lazy"
                  allowFullScreen
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>

              {/* Address card */}
              <div style={{ background: T.bgCard, border: `1.5px solid ${T.border}`, borderRadius: '20px', padding: mob ? '22px 20px' : '28px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                  <div style={{ width: '42px', height: '42px', background: T.bgDark, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <i className="fas fa-map-marker-alt" style={{ fontSize: '16px', color: T.accent }} />
                  </div>
                  <div>
                    <div style={{ fontFamily: T.sans, fontWeight: 700, fontSize: '15px', color: T.text, marginBottom: '4px' }}>Our Office</div>
                    <div style={{ fontFamily: T.sans, fontSize: '13px', color: T.soft, lineHeight: 1.65 }}>
                      Subramaniya Siva Street,<br />Chennai, Tamil Nadu, India
                    </div>
                  </div>
                </div>

                <div style={{ paddingTop: '18px', borderTop: `1px solid ${T.borderL}`, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  {[
                    { icon: 'fa-clock',        label: 'Hours',     value: '24 / 7 / 365' },
                    { icon: 'fa-headset',      label: 'Support',   value: 'Always On' },
                    { icon: 'fa-car',          label: 'Fleet',     value: '180+ Vehicles' },
                    { icon: 'fa-star',         label: 'Rating',    value: '4.9 ★ Google' },
                  ].map(({ icon, label, value }) => (
                    <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <i className={`fas ${icon}`} style={{ fontSize: '13px', color: T.accentD, width: '16px', flexShrink: 0 }} />
                      <div>
                        <div style={{ fontFamily: T.sans, fontSize: '10px', color: T.softer, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</div>
                        <div style={{ fontFamily: T.sans, fontSize: '13px', fontWeight: 700, color: T.text, marginTop: '1px' }}>{value}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── CTA BANNER ───────────────────────────────────────────────────── */}
      <section style={{ background: T.bgDark, padding: mob ? '60px 20px' : '88px 60px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '-80px', right: '-80px', width: '380px', height: '380px', borderRadius: '50%', background: T.accent, opacity: 0.07, filter: 'blur(80px)', pointerEvents: 'none' }} />
        <div style={{ maxWidth: maxW, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'grid', gridTemplateColumns: mob ? '1fr' : '1fr auto', gap: mob ? '36px' : '60px', alignItems: 'center' }}>
            <div>
              <SectionIntro
                light
                tag="Ready to Go?"
                title={<>Book Your Ride<br /><em style={{ fontStyle: 'italic', color: T.accent }}>Today.</em></>}
                subtitle="Call us or WhatsApp — we're available 24/7 across Chennai and Tamil Nadu."
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', flexShrink: 0 }}>
              <a href="tel:+918637658382" style={{ fontFamily: T.sans, fontSize: '14px', fontWeight: 700, background: T.accent, color: T.text, padding: '16px 32px', borderRadius: '50px', textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', boxShadow: '0 4px 22px rgba(253,219,28,0.28)', whiteSpace: 'nowrap', transition: 'transform 0.18s, box-shadow 0.18s' }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 30px rgba(253,219,28,0.42)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 22px rgba(253,219,28,0.28)'; }}>
                <i className="fas fa-phone" /> Call to Book
              </a>
              <a href="https://wa.me/918637658382?text=Hi%20Saha%20Travels%2C%20I%20want%20to%20book%20a%20ride" style={{ fontFamily: T.sans, fontSize: '14px', fontWeight: 600, background: 'rgba(255,255,255,0.07)', color: '#fff', padding: '16px 32px', borderRadius: '50px', textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', border: '1.5px solid rgba(255,255,255,0.14)', whiteSpace: 'nowrap', transition: 'background 0.2s, border-color 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(37,211,102,0.12)'; e.currentTarget.style.borderColor = 'rgba(37,211,102,0.4)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.14)'; }}>
                <i className="fab fa-whatsapp" style={{ color: '#25D366', fontSize: '15px' }} /> WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── GLOBAL STYLES ────────────────────────────────────────────────── */}
      <style>{`
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        body {
          background: #FAFAF7;
          font-family: 'Bricolage Grotesque', system-ui, sans-serif;
          overflow-x: hidden;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }
        img { max-width: 100%; display: block; }
        a { transition: opacity 0.18s; }
        ::selection { background: #FDDB1C; color: #111827; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: #F2EDE4; }
        ::-webkit-scrollbar-thumb { background: #C9C0B0; border-radius: 2px; }
        ::-webkit-scrollbar-thumb:hover { background: #FDDB1C; }

        @keyframes marquee {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-33.333%); }
        }
        @keyframes pulse-green {
          0%, 100% { box-shadow: 0 0 0 2px rgba(16,185,129,0.25); }
          50%       { box-shadow: 0 0 0 5px rgba(16,185,129,0.1); }
        }

        input::placeholder, textarea::placeholder { color: #9CA3AF; }

        button, input, textarea, a {
          touch-action: manipulation;
          -webkit-tap-highlight-color: transparent;
        }

        * { -webkit-tap-highlight-color: transparent; }
      `}</style>
    </>
  );
};

export default Contact;